// Aba DISPOSITIVOS CONECTADOS (Editar Usuário) — dois canais reais e
// independentes: sessões web (refresh_tokens ativos do GPS Drive Backend,
// ver gpsdrive-user-sessions.service.ts) e tokens de push nativo (app
// Android, ver gpsdrive-user-fcm-tokens.service.ts — pedido 2026-09-11:
// "tem que ter o token gerado, mostrar cada telefone e quando o usuário
// acessou o app"). Um usuário pode ter só um dos dois, os dois, ou
// nenhum — nunca a mesma lista.
//
// "Desconectar" (sessão web) revoga o refresh token de verdade: a próxima
// renovação silenciosa daquele navegador/aparelho falha e ele é
// deslogado — não existe blocklist de access token (stateless), então um
// token JÁ emitido continua válido até expirar naturalmente, é uma
// desconexão que vale a partir da próxima renovação, não instantânea numa
// aba já aberta. "Remover" (token push) para as notificações push nativas
// pra aquele aparelho até ele reabrir o app autenticado (re-registra
// sozinho, ver useNativePushRegistration).
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Bell, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { describeUserAgent } from "@/lib/user-agent";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import {
  useRevokeUserFcmToken,
  useRevokeUserSession,
  useUserFcmTokens,
  useUserSessions,
} from "@/hooks/queries";

function maskToken(token: string): string {
  if (token.length <= 24) return token;
  return `${token.slice(0, 12)}···${token.slice(-8)}`;
}

export function UserConnectedDevicesTab({ userId }: { userId: string | undefined }) {
  const { data: sessions, isLoading: loadingSessions } = useUserSessions(userId);
  const { data: fcmTokens, isLoading: loadingFcmTokens } = useUserFcmTokens(userId);
  const revokeSession = useRevokeUserSession();
  const revokeFcmToken = useRevokeUserFcmToken();
  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null);
  const [revokingTokenId, setRevokingTokenId] = useState<string | null>(null);

  const handleDisconnect = async (sessionId: string) => {
    if (!userId) return;
    setRevokingSessionId(sessionId);
    try {
      await revokeSession.mutateAsync({ userId, sessionId });
      toast.success("Dispositivo desconectado");
    } catch (err) {
      toast.error("Não foi possível desconectar", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    } finally {
      setRevokingSessionId(null);
    }
  };

  const handleRemoveToken = async (tokenId: string) => {
    if (!userId) return;
    setRevokingTokenId(tokenId);
    try {
      await revokeFcmToken.mutateAsync({ userId, tokenId });
      toast.success("Token removido");
    } catch (err) {
      toast.error("Não foi possível remover o token", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    } finally {
      setRevokingTokenId(null);
    }
  };

  const handleCopyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      toast.success("Token copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  if (loadingSessions || loadingFcmTokens) {
    return <div className="py-16 text-center text-xs text-muted-foreground">Carregando…</div>;
  }

  const hasSessions = sessions && sessions.length > 0;
  const hasFcmTokens = fcmTokens && fcmTokens.length > 0;

  if (!hasSessions && !hasFcmTokens) {
    return (
      <div className="rounded-lg border border-dashed border-border py-16 text-center text-xs text-muted-foreground">
        Nenhum dispositivo conectado no momento.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Sessões web
        </div>
        {!hasSessions ? (
          <p className="text-xs text-muted-foreground">Nenhuma sessão web ativa.</p>
        ) : (
          <div className="space-y-2">
            {sessions.map((session) => (
              <div key={session.id} className="flex items-center gap-3 rounded-lg border border-border bg-background p-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
                  <Smartphone className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1 text-xs">
                  <div className="font-semibold text-foreground">
                    {describeUserAgent(session.userAgent) ?? "Dispositivo desconhecido"}
                  </div>
                  <div className="text-muted-foreground">
                    Conectado {formatDistanceToNow(new Date(session.createdAt), { addSuffix: true, locale: ptBR })}
                  </div>
                  <div className="text-muted-foreground">
                    Sessão válida até {new Date(session.expiresAt).toLocaleString("pt-BR")}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={revokingSessionId === session.id}
                  onClick={() => handleDisconnect(session.id)}
                >
                  {revokingSessionId === session.id ? "Desconectando…" : "Desconectar"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          App nativo (notificações push)
        </div>
        {!hasFcmTokens ? (
          <p className="text-xs text-muted-foreground">Nenhum token de push registrado.</p>
        ) : (
          <div className="space-y-2">
            {fcmTokens.map((t) => (
              <div key={t.id} className="flex items-center gap-3 rounded-lg border border-border bg-background p-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
                  <Bell className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1 text-xs">
                  <button
                    type="button"
                    onClick={() => handleCopyToken(t.token)}
                    title="Clique para copiar o token completo"
                    className="block truncate rounded font-mono font-semibold text-foreground hover:text-sky-600"
                  >
                    {maskToken(t.token)}
                  </button>
                  <div className="text-muted-foreground">
                    Último acesso {formatDistanceToNow(new Date(t.lastSeenAt), { addSuffix: true, locale: ptBR })}
                  </div>
                  <div className="text-muted-foreground">
                    Registrado em {new Date(t.createdAt).toLocaleString("pt-BR")}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={revokingTokenId === t.id}
                  onClick={() => handleRemoveToken(t.id)}
                >
                  {revokingTokenId === t.id ? "Removendo…" : "Remover"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
